
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ShareModal from '../../components/feature/ShareModal';
import { productsData } from '../../mocks/products';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';

const DealsPage = () => {
  const [sortBy, setSortBy] = useState<'discount' | 'price' | 'savings'>('discount');
  const [trackedAnimation, setTrackedAnimation] = useState<number[]>([]);
  const [shareModal, setShareModal] = useState<{ isOpen: boolean; product: typeof dealsProducts[0] | null }>({
    isOpen: false,
    product: null
  });
  const { addToTracking, isTracked } = usePriceMonitor();

  const dealsProducts = useMemo(() => {
    const productsWithDeals = productsData.map(product => {
      const lowestRetailer = product.retailers.reduce((min, r) => r.price < min.price ? r : min);
      const savings = product.highestPrice - lowestRetailer.price;
      const discountPercent = Math.round((savings / product.highestPrice) * 100);
      
      return {
        ...product,
        currentPrice: lowestRetailer.price,
        lowestRetailer,
        savings,
        discountPercent,
      };
    });

    return productsWithDeals.sort((a, b) => {
      switch (sortBy) {
        case 'discount':
          return b.discountPercent - a.discountPercent;
        case 'price':
          return a.currentPrice - b.currentPrice;
        case 'savings':
          return b.savings - a.savings;
        default:
          return 0;
      }
    });
  }, [sortBy]);

  const handleTrackPrice = (product: typeof dealsProducts[0]) => {
    addToTracking({
      id: product.id,
      name: product.name,
      image: product.image,
      currentPrice: product.currentPrice,
      retailer: product.lowestRetailer.name,
    });
    
    setTrackedAnimation(prev => [...prev, product.id]);
    setTimeout(() => {
      setTrackedAnimation(prev => prev.filter(id => id !== product.id));
    }, 1000);
  };

  const handleShare = (product: typeof dealsProducts[0]) => {
    setShareModal({ isOpen: true, product });
  };

  const topDeal = dealsProducts[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-4">
              <i className="ri-fire-fill text-yellow-300"></i>
              <span className="text-sm font-medium">Hot Deals</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">Today&apos;s Best Deals</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Discover the biggest price drops across all categories. Updated hourly.
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 lg:gap-16">
            <div className="text-center">
              <p className="text-3xl font-bold">{dealsProducts.length}</p>
              <p className="text-sm text-white/80">Active Deals</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">Up to {Math.max(...dealsProducts.map(p => p.discountPercent))}%</p>
              <p className="text-sm text-white/80">Max Discount</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">${Math.max(...dealsProducts.map(p => p.savings))}</p>
              <p className="text-sm text-white/80">Max Savings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Deal */}
      {topDeal && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 px-4 py-2 bg-red-500 text-white rounded-full text-sm font-bold">
                    -{topDeal.discountPercent}% OFF
                  </div>
                  <img
                    src={topDeal.image}
                    alt={topDeal.name}
                    className="w-64 h-64 object-contain object-top"
                  />
                </div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                    Deal of the Day
                  </span>
                  <span className="text-sm text-gray-500">{topDeal.category}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{topDeal.name}</h2>
                <p className="text-gray-600 mb-4 line-clamp-2">{topDeal.description}</p>
                
                <div className="flex items-end gap-3 mb-4">
                  <span className="text-4xl font-bold text-gray-900">${topDeal.currentPrice}</span>
                  <span className="text-xl text-gray-400 line-through">${topDeal.highestPrice}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                    Save ${topDeal.savings}
                  </span>
                </div>

                <p className="text-sm text-gray-500 mb-6">
                  Best price at <strong>{topDeal.lowestRetailer.name}</strong>
                </p>

                <div className="flex gap-3">
                  <Link
                    to={`/product/${topDeal.id}`}
                    className="flex-1 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors text-center cursor-pointer whitespace-nowrap"
                  >
                    View Deal
                  </Link>
                  <button
                    onClick={() => handleTrackPrice(topDeal)}
                    disabled={isTracked(topDeal.id)}
                    className={`px-6 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                      isTracked(topDeal.id)
                        ? 'bg-teal-100 text-teal-700'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {isTracked(topDeal.id) ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-check-line"></i>
                        Tracking
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <i className="ri-line-chart-line"></i>
                        Track
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => handleShare(topDeal)}
                    className="px-6 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-share-line"></i>
                      Share
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deals Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Sort Options */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold text-gray-900">All Deals</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSortBy('discount')}
                className={`px-4 py-2 text-sm rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'discount' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Biggest Discount
              </button>
              <button
                onClick={() => setSortBy('savings')}
                className={`px-4 py-2 text-sm rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'savings' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Most Savings
              </button>
              <button
                onClick={() => setSortBy('price')}
                className={`px-4 py-2 text-sm rounded-md transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === 'price' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Lowest Price
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {dealsProducts.slice(1).map((product) => {
            const tracked = isTracked(product.id);
            
            return (
              <div
                key={product.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
              >
                <Link to={`/product/${product.id}`} className="block cursor-pointer">
                  <div className="aspect-square bg-gray-50 p-4 relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain object-top group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full">
                      -{product.discountPercent}%
                    </div>
                    <div className="absolute top-3 right-3 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                      Save ${product.savings}
                    </div>
                  </div>
                </Link>
                <div className="p-5">
                  <p className="text-xs text-gray-500 mb-1">{product.category}</p>
                  <Link to={`/product/${product.id}`} className="cursor-pointer">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 hover:text-teal-600 transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  
                  {/* Price */}
                  <div className="flex items-end gap-2 mb-3">
                    <span className="text-2xl font-bold text-gray-900">${product.currentPrice}</span>
                    <span className="text-sm text-gray-400 line-through">${product.highestPrice}</span>
                  </div>

                  <p className="text-xs text-gray-500 mb-4">
                    at {product.lowestRetailer.name}
                  </p>

                  {/* Price History Mini */}
                  <div className="mb-4">
                    <div className="h-1.5 bg-gray-200 rounded-full relative">
                      <div
                        className="absolute h-full bg-gradient-to-r from-green-400 to-teal-500 rounded-full"
                        style={{
                          width: `${((product.currentPrice - product.lowestPrice) / (product.highestPrice - product.lowestPrice)) * 100}%`
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                      <span>${product.lowestPrice}</span>
                      <span>${product.highestPrice}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Link
                      to={`/product/${product.id}`}
                      className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors text-center cursor-pointer whitespace-nowrap"
                    >
                      View Deal
                    </Link>
                    <button
                      onClick={() => handleTrackPrice(product)}
                      disabled={tracked}
                      className={`px-4 py-2.5 rounded-lg transition-all cursor-pointer ${
                        tracked
                          ? 'bg-teal-100 text-teal-700'
                          : trackedAnimation.includes(product.id)
                          ? 'bg-teal-600 text-white scale-95'
                          : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {tracked ? (
                        <i className="ri-check-line"></i>
                      ) : (
                        <i className="ri-line-chart-line"></i>
                      )}
                    </button>
                    <button
                      onClick={() => handleShare(product)}
                      className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-all cursor-pointer"
                    >
                      <i className="ri-share-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-8 lg:p-12 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-send-line text-3xl"></i>
          </div>
          <h3 className="text-2xl font-bold mb-3">Never Miss a Deal</h3>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Get personalized deal alerts delivered to your inbox. Be the first to know when prices drop on products you care about.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-teal-600 rounded-lg font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line"></i>
            Browse All Products
          </Link>
        </div>
      </div>

      <Footer />

      {/* Share Modal */}
      {shareModal.product && (
        <ShareModal
          isOpen={shareModal.isOpen}
          onClose={() => setShareModal({ isOpen: false, product: null })}
          title={`${shareModal.product.name} - ${shareModal.product.discountPercent}% OFF!`}
          url={`${window.location.origin}/product/${shareModal.product.id}`}
          description={`Save $${shareModal.product.savings} on ${shareModal.product.name} at ${shareModal.product.lowestRetailer.name}!`}
          image={shareModal.product.image}
        />
      )}
    </div>
  );
};

export default DealsPage;
